import React from 'react';
import styled from 'styled-components';
import { SwipeableListItem } from '../../../../../generic/swipe/SwipeableListAdapter';
import { SwipeAction } from '../../../../../generic/ui/SwipeAction';
import { CardRow } from '../../../../../generic/ui/CardRow';
import { Mono } from '../../../../../generic/ui/Mono';
import PencilIcon from '../../../../../generic/icons/IconLib/PencilIcon';
import TrashIcon from '../../../../../generic/icons/IconLib/TrashIcon';
import PackageBoxIcon from '../../../../../generic/icons/IconLib/PackageBoxIcon';
import ScalesIcon from '../../../../../generic/icons/IconLib/ScaleIcon';
import { IPackagesItem } from '../../../../../modules/API/Defaults/models/packages';
import { IPalletDetailes } from '../../../../../modules/API/Pallets/models/palletList';
import { EXIST_PATH } from '../../../../../modules/API/settings';

interface Props {
    item: IPalletDetailes;
    packageHash?: Record<number, IPackagesItem>;
    handleSelectProduct?: (item: any) => void;
    deleteProduct: (palletNum: number, recId: number) => void;
    isAllowToEdit: boolean;
    isLast?: boolean;
}

const PalletDetailRow: React.FC<Props> = ({
    item,
    handleSelectProduct,
    deleteProduct,
    isAllowToEdit,
    isLast = false,
}) => {
    const {
        ProductDesc,
        VarietyDesc,
        SizeID,
        Quantity,
        WeightBuy,
        Pack_Name,
        InvoiceBuy,
        InvoiceSale,
        RecID,
        Pallet_Num,
        GeneralProductID,
    } = item;

    const isInvoiced = InvoiceBuy !== 0 || InvoiceSale !== 0;
    const canEdit = isAllowToEdit && InvoiceBuy === 0 && !!handleSelectProduct;
    const canDelete = isAllowToEdit && !isInvoiced;

    const productName = `${ProductDesc?.trim()} ${VarietyDesc?.trim()} ${SizeID?.trim()}`.trim();
    const productImg = GeneralProductID
        ? `${EXIST_PATH}/images/products/${GeneralProductID}.png`
        : '/emptyBox.png';
    console.log(canEdit, canDelete);
    const handleEdit = () => {
        if (!canEdit) return;
        handleSelectProduct?.(item);
    };
    const handleDelete = () => {
        if (!canDelete) return;
        deleteProduct(Pallet_Num, RecID);
    };

    return (
        <Wrapper>
            <SwipeableListItem
                swipeRight={
                    canEdit
                        ? {
                              content: (
                                  <SwipeAction
                                      variant='edit'
                                      align='start'
                                      icon={<PencilIcon />}
                                      label='ערוך'
                                  />
                              ),
                              action: handleEdit,
                          }
                        : undefined
                }
                swipeLeft={
                    canDelete
                        ? {
                              content: (
                                  <SwipeAction
                                      variant='delete'
                                      align='end'
                                      icon={<TrashIcon />}
                                      label='מחק'
                                  />
                              ),
                              action: handleDelete,
                          }
                        : undefined
                }
                threshold={0.3}
                blockSwipe={!canEdit && !canDelete}
            >
                <CardRow
                    $columns='minmax(0, 1fr) auto'
                    $mobileColumns='minmax(0, 1fr) auto'
                    $padding='0.5rem 0.75rem'
                    $mobilePadding='0.5rem 0.625rem'
                    $minHeight='3.25rem'
                    $gap='0.75rem'
                    $isLast={isLast}
                    $hover={false}
                >
                    <CardRow.Cell>
                        <CardRow.Stack $gap='0.25rem'>
                            <Name>{productName}</Name>
                            <CardRow.Line $gap='0.875rem' $wrap>
                                <MetaItem>
                                    <MetaIcon>
                                        <ScalesIcon />
                                    </MetaIcon>
                                    <MetaText>
                                        <Mono>{WeightBuy}</Mono>
                                    </MetaText>
                                </MetaItem>
                                {Pack_Name && (
                                    <MetaItem>
                                        <MetaIcon>
                                            <PackageBoxIcon />
                                        </MetaIcon>
                                        <MetaText>
                                            {Pack_Name.trim()}
                                            {' · '}
                                            <Mono>{Quantity}</Mono>
                                        </MetaText>
                                    </MetaItem>
                                )}
                            </CardRow.Line>
                        </CardRow.Stack>
                    </CardRow.Cell>

                    <CardRow.Cell $align='center'>
                        <ProductImg
                            src={productImg}
                            alt={productName}
                            loading='lazy'
                            decoding='async'
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src = '/emptyBox.png';
                            }}
                        />
                    </CardRow.Cell>
                </CardRow>
            </SwipeableListItem>
        </Wrapper>
    );
};

export default PalletDetailRow;

const Wrapper = styled.div`
    position: relative;
    width: 100%;
`;

const Name = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const MetaItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: ${({ theme }) => theme.colors.ink3};
`;

const MetaIcon = styled.span`
    color: ${({ theme }) => theme.colors.icon};
    display: inline-flex;
    align-items: center;

    svg {
        width: 0.8rem; height: 0.8rem;
    }

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`;

const MetaText = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const ProductImg = styled.img`
    width: 2.5rem;
    height: 2.5rem;
    object-fit: contain;
    border-radius: ${({ theme }) => theme.radii.sm};
    user-select: none;
    pointer-events: none;
    flex-shrink: 0;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 2.25rem;
        height: 2.25rem;
    }
`;
